/**
 * Statistical Report API service (統計一覧表 — /rezepts/statistical_init).
 *
 * Slice 2: massager dropdown.
 * Slices 3-6: search, printOther, printMenuMassagers, printList added later.
 */

import { apiClient } from '@/shared/lib/axios';
import { clinicApiPath } from '@/shared/lib/apiPath';
import { SERVICE_TYPE_A2 } from '@/constants/serviceType';
import type {
  StatisticalMassagerOption,
  StatisticalSearchResponse,
} from '@/pages/main/rezepts/statistical/types';

export interface StatisticalMassagersResponse {
  data: { data: StatisticalMassagerOption[] };
}

interface PreviewTicketResponse {
  data: { url: string };
}

export async function createStatisticalListPdfPreviewUrl(
  siteType: string,
  clinicNumber: string,
  yearmonth: string,
  massagerIds: (number | 'all')[],
  outputOption: boolean,
  outputOption2: boolean,
  serviceType: string = SERVICE_TYPE_A2,
): Promise<string> {
  const massagerIdValues = massagerIds.map(id => (id === 'all' ? 'all' : String(id)));
  const response = await apiClient.post<PreviewTicketResponse>(
    clinicApiPath(siteType, clinicNumber, '/rezepts/statistical/print-list/preview-ticket'),
    {
      yearmonth,
      massager_id: massagerIdValues.join(','),
      massager_ids: massagerIdValues,
      output_option: outputOption ? 1 : 0,
      output_option2: outputOption2 ? 1 : 0,
    },
    { headers: { 'X-Service-Type': serviceType } },
  );
  return response.data.data.url;
}

export async function createStatisticalBillingListPdfPreviewUrl(
  siteType: string,
  clinicNumber: string,
  yearmonth: string,
  serviceType: string = SERVICE_TYPE_A2,
): Promise<string> {
  const response = await apiClient.post<PreviewTicketResponse>(
    clinicApiPath(siteType, clinicNumber, '/rezepts/statistical/print-billing-list/preview-ticket'),
    { yearmonth },
    { headers: { 'X-Service-Type': serviceType } },
  );
  return response.data.data.url;
}

export async function createStatisticalOtherPdfPreviewUrl(
  siteType: string,
  clinicNumber: string,
  yearmonth: string,
  massagerId: number | null,
  pdfPattern: '1' | '2' | '3' | '4',
  serviceType: string = SERVICE_TYPE_A2,
): Promise<string> {
  const payload: Record<string, string | number> = { yearmonth, pdf_pattern: pdfPattern };
  if (massagerId !== null) payload.massager_id = massagerId;

  const response = await apiClient.post<PreviewTicketResponse>(
    clinicApiPath(siteType, clinicNumber, '/rezepts/statistical/print-other/preview-ticket'),
    payload,
    { headers: { 'X-Service-Type': serviceType } },
  );
  return response.data.data.url;
}

export async function fetchStatisticalSearch(
  siteType: string,
  clinicNumber: string,
  yearmonth: string,
  massagerId: number | null,
  serviceType: string = SERVICE_TYPE_A2,
): Promise<StatisticalSearchResponse> {
  const params: Record<string, string | number> = { yearmonth };
  if (massagerId !== null) params.massager_id = massagerId;

  const response = await apiClient.get<StatisticalSearchResponse>(
    clinicApiPath(siteType, clinicNumber, '/rezepts/statistical/search'),
    { params, headers: { 'X-Service-Type': serviceType } },
  );
  return response.data;
}

export async function fetchStatisticalMassagers(
  siteType: string,
  clinicNumber: string,
  yearmonth: string,
  serviceType: string = SERVICE_TYPE_A2,
): Promise<StatisticalMassagersResponse> {
  const response = await apiClient.get<StatisticalMassagersResponse>(
    clinicApiPath(siteType, clinicNumber, '/rezepts/statistical/massagers'),
    {
      params: { yearmonth },
      headers: { 'X-Service-Type': serviceType },
    },
  );
  return response.data;
}
